// TypeScript interfaces for Work Request module

export interface WorkRequestData {
  id: string;
  workRequestNumber: string;
  workRequestTitle: string;
  description?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  requestFor: RequestForType;
  plannedStartDate: string;
  plannedEndDate: string;
  approvalRequired: boolean;
  status: WorkRequestStatus;
  workLines: WorkRequestLine[];
  totalLines: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  approverComments?: string;
  rejectionComments?: string;
  workRequestApprovals?: any[];
  approvals?: any[];
  approvalStatus?: string;
}

export interface WorkRequestLine {
  id: string;
  workRequestLineId?: string | number; // For edit mode
  taskTitle: string;
  taskDescription?: string;
  category: string;
  subcategory?: string;
  workCategoryId?: string | number; // For API submissions
  workSubCategoryId?: string | number; // For API submissions
  quantity: number;
  uom: string;
  uomId?: string | number; // For API submissions
  source: WorkLineSource;
  sourceId?: string;
  assignedTower?: string;
  assignedVilla?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  documents?: string[];
  materialAllocation?: MaterialAllocation[];
}

export interface MaterialAllocation {
  materialId: string;
  materialName: string;
  allocatedQuantity: number;
  uom: string;
}

export interface ProjectTask {
  taskId: string;
  taskName: string;
  description: string;
  quantity: number;
  uom: string;
  plannedStart: string;
  plannedEnd: string;
  activityId: string;
  activityName: string;
  projectId: string;
}

export interface WorkBoqLine {
  lineId: string;
  category: string;
  subcategory: string;
  taskName: string;
  quantity: number;
  uom: string;
  towerFlatInfo: string;
  documents: string[];
  workBoqId?: string;
  materialAllocation?: WorkBoqMaterialAllocation[];
}

export interface WorkBoqMaterialAllocation {
  workBoqLineMetId: number;
  materialId: number;
  categoryId: number;
  allocatedQty: number;
  materialName: string;
  categoryName?: string;
  uomName?: string;
}

export interface Activity {
  id: string;
  name: string;
  projectId: string;
  description?: string;
}

export interface WorkBoq {
  id: string;
  title: string;
  siteId: string;
  projectId?: string;
  status: string;
}

export interface Site {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  name: string;
  siteId: string;
}

export interface Category {
  id: string;
  name: string;
  parentId?: string;
  description?: string;
}

export interface UOM {
  id: string;
  name: string;
  code: string;
}

export interface MasterData {
  sites: Site[];
  projects: Project[];
  activities: Activity[];
  workBoqs: WorkBoq[];
  categories: Category[];
  uomList: UOM[];
}

// Enums
export enum RequestForType {
  ConstructionWork = "Construction Work",
  Service = "Service",
  Maintenance = "Maintenance",
  Others = "Others"
}

export enum WorkRequestStatus {
  Draft = "Draft",
  PendingApproval = "Pending Approval",
  Approved = "Approved",
  Rejected = "Rejected",
  InProgress = "In Progress",
  Completed = "Completed"
}

export enum WorkLineSource {
  ProjectManagement = "Project Management",
  WorkBOQ = "Work BOQ",
  NewWorkLine = "New Work Line"
}

// Form interfaces
export interface WorkRequestFormData {
  // General Information
  workRequestId?: string | number; // For edit mode
  workRequestNumber: string;
  workRequestTitle: string;
  description?: string;
  siteId: string;
  siteName?: string;
  projectId?: string;
  projectName?: string;
  requestFor: RequestForType;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  approvalRequired: string; // "true" | "false" for form handling
  approvalsData?: any;
  isStrictApproval?: string | boolean;
  // Work Request Lines
  workRequestLines: WorkRequestLine[];
  selectedSource?: WorkLineSource; // Retain source selection
  isSourceLocked?: boolean; // Track if source is locked

  // Internal fields
  status?: WorkRequestStatus;
  type?: "create" | "edit" | "duplicate";
  createdBy?: string;
  createdAt?: string;
}

// Filter interfaces
export interface WorkRequestFilters {
  searchText?: string;
  siteIds?: string[];
  statuses?: WorkRequestStatus[];
  requestFor?: RequestForType[];
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  createdBy?: string[];
}

// API Response interfaces (for consistency, even with mock data)
export interface ApiResponse<T> {
  data: T;
  status: boolean;
  message: string;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

// Component Props interfaces
export interface WorkRequestTableProps {
  data: WorkRequestData[];
  loading?: boolean;
  onEdit?: (record: WorkRequestData) => void;
  onView?: (record: WorkRequestData) => void;
  onDelete?: (record: WorkRequestData) => void;
  onSubmit?: (record: WorkRequestData) => void;
  onApprove?: (record: WorkRequestData) => void;
  onReject?: (record: WorkRequestData) => void;
}

export interface SourceSelectorProps {
  projectId?: string;
  workBoqId?: string;
  onLinesSelected: (lines: WorkRequestLine[]) => void;
  selectedLines?: WorkRequestLine[];
  editingLine?: WorkRequestLine;
}

// Step component props
export interface StepComponentProps {
  next?: (data?: any) => void;
  prev?: (data?: any) => void;
  current?: number;
  [key: string]: any;
}